import styles from './ContractsLayout.module.scss'
import { routes } from 'types/routes.ts'
import SeoTags from 'components/seo-tags/SeoTags.tsx'
import { keyFromSelector } from 'i18next'
import { useTranslation } from 'react-i18next'
import { PropsWithChildren } from 'react'
import { ContractsLayoutProps, FlowSteps } from 'features/contracts/layout/types.ts'
import { useLocation, useNavigate } from 'react-router'
import { Button, StepIndicator } from '@boltenergy-be/design-system'
import { AddContractSteps } from '../add/types'
import { TerminateSteps } from '../terminate/types'
import SupportFooter from 'layouts/components/support-footer/SupportFooter.tsx'

const ContractsLayout = <Steps extends FlowSteps | undefined>({
  flow,
  steps,
  currentStep,
  children,
  label,
  setCurrentStep
}: PropsWithChildren<ContractsLayoutProps<Steps>>) => {
  // i18n
  const { t } = useTranslation(['contracts', 'common'])

  // Router
  const navigate = useNavigate()
  const { pathname } = useLocation()

  // Constants
  const showNav = !!steps && currentStep !== steps[steps.length - 1]
  const stepsWithoutLast = steps?.slice(0, -1) || []
  const isLastStep = steps?.[steps.length - 1] === currentStep
  const activeStepIndex = stepsWithoutLast.findIndex((step) => step === currentStep)

  /**
   * Handles exit button click
   */
  const handleExit = () => {
    void navigate(routes.MOVE)
  }

  /**
   * Returns previous step based on the current step
   *
   * @return {string}
   */
  const getPreviousStep = (): Steps | undefined => {
    const currentIndex = steps?.findIndex((step) => step === currentStep)
    return currentIndex ? steps?.[currentIndex - 1] : undefined
  }

  return (
    <>
      <SeoTags title={t(($) => $.seo[flow || 'index'])} />

      <header className={styles.header}>
        {pathname === routes.CONTRACTS || isLastStep ? (
          <div />
        ) : (
          <Button
            type="button"
            variant="tertiary"
            leadingIcon="arrowLeft"
            onClick={() => {
              if (currentStep === AddContractSteps.ADDRESS || currentStep === TerminateSteps.CHURN) {
                void navigate(-1)
              } else if (setCurrentStep) {
                const prevStep = getPreviousStep()
                if (prevStep) {
                  setCurrentStep(prevStep)
                }
              }
            }}
          />
        )}

        <div className={styles['indicator-group']}>
          <>
            <strong>{label}</strong>
            {showNav && (
              <>
                <small>
                  {t(($) => $.header.indicator, {
                    current: activeStepIndex + 1,
                    total: stepsWithoutLast.length
                  })}
                </small>
                <StepIndicator activeIndex={activeStepIndex} totalSteps={stepsWithoutLast.length} classnames={{ list: styles.indicator }} />
              </>
            )}
          </>
        </div>

        <Button variant="tertiary" leadingIcon="cross" onClick={handleExit} />
      </header>

      <main className={styles.main}>
        <div className={styles.wrapper}>{children}</div>
      </main>

      <SupportFooter ns="contracts" i18nKey={keyFromSelector(($) => $.footer, { ns: 'contracts' })} />
    </>
  )
}

export default ContractsLayout
